<template>
  <div class="lesson-rating">
    <div class="lesson-rating__buttons" v-if="!popupOpened">
      <span class="lesson-rating__icon lesson-rating__icon--desktop" @click="handleRating(true)">
        <font-awesome-icon :icon="['fal', 'thumbs-up']" v-if="is_liked !== true" />
        <font-awesome-icon :icon="['fas', 'thumbs-up']" v-if="is_liked === true" />
      </span>
      <span
        class="lesson-rating__icon lesson-rating__icon--down lesson-rating__icon--desktop"
        @click="handleRating(false)">
        <font-awesome-icon
          class="lesson-rating__icon--invert"
          :icon="['fal', 'thumbs-down']"
          v-if="is_liked !== false" />
        <font-awesome-icon
          class="lesson-rating__icon--invert"
          :icon="['fas', 'thumbs-down']"
          v-if="is_liked === false" />
      </span>

      <span class="lesson-rating__icon lesson-rating__icon--mobile" @click="openPopup">
        <font-awesome-icon :icon="['fal', 'thumbs-up']" v-if="is_liked === null" />
        <font-awesome-icon :icon="['fas', 'thumbs-up']" v-if="is_liked === true" />
        <font-awesome-icon :icon="['fas', 'thumbs-down']" v-if="is_liked === false" />
        <p class="lesson-rating__text">{{ rateText }}</p>
      </span>
    </div>

    <div class="lesson-rating__popup-close" v-if="popupOpened" @click="closePopup">
      <font-awesome-icon :icon="['fa', 'xmark']" />
    </div>

    <div class="lesson-rating__popup" v-if="popupOpened" :class="{ 'lesson-rating__popup--large': !is_liked }">
      <div class="lesson-rating__popup-buttons">
        <div class="lesson-rating__popup-button" :class="{ white: is_liked === true }" @click="handleRating(true)">
          <span class="lesson-rating__popup-icon">
            <font-awesome-icon :icon="['fal', 'thumbs-up']" v-if="is_liked !== true" />
            <font-awesome-icon :icon="['fas', 'thumbs-up']" v-if="is_liked === true" />
          </span>
          <p>{{ $t('rating.iLikeThis') }}</p>
        </div>
        <div class="lesson-rating__popup-button" :class="{ white: is_liked === false }" @click="handleRating(false)">
          <span class="lesson-rating__popup-icon">
            <font-awesome-icon
              class="lesson-rating__icon--invert"
              :icon="['fal', 'thumbs-down']"
              v-if="is_liked !== false" />
            <font-awesome-icon
              class="lesson-rating__icon--invert"
              :icon="['fas', 'thumbs-down']"
              v-if="is_liked === false" />
          </span>
          <p>{{ $t('rating.couldImprove') }}</p>
        </div>
      </div>

      <div class="lesson-rating__popup-input" v-if="!is_liked">
        <auto-sized-textarea ref="autoSizer">
          <textarea v-model="feedback" rows="1" :placeholder="$t('rating.whatToImprove')" />
        </auto-sized-textarea>
        <button :disabled="feedback.length === 0" @click="submitFeedback">
          <font-awesome-icon :icon="['fa', 'check']" />
        </button>
      </div>
    </div>
  </div>
  <div class="lesson-rating__popup-overlay" v-if="popupOpened" @click="closePopup"></div>
</template>

<script>
import { mapState } from 'vuex';
import AutoSizedTextarea from '@shared/components/utils/AutoSizedTextarea.vue';
import appClient from '@shared/api/appClient';

export default {
  name: 'LessonRatings',
  components: {
    AutoSizedTextarea,
  },
  data() {
    return {
      popupOpened: false,
      feedback: '',
    };
  },
  computed: {
    ...mapState('lesson', {
      is_liked: (state) => state.is_liked,
      lesson: (state) => state.lesson,
    }),
    rateText() {
      return this.is_liked !== null ? this.$t('rating.rated') : this.$t('rating.rate');
    },
  },
  methods: {
    handleRating(isLiked) {
      if (this.is_liked === isLiked) {
        this.deleteRating();
      } else {
        this.rateLesson(isLiked);
      }
      this.trackEvent('button_clicked', {
        button_name: isLiked ? 'thumbs_up' : 'thumbs_down',
        lesson_id: this.lesson.id,
      });
    },
    deleteRating() {
      const previousRating = this.is_liked;

      this.$store.dispatch('lesson/setIsLiked', null);
      if (this.lesson) {
        this.lesson.is_liked = null;
      }

      this.popupOpened = false;

      this.trackEvent('lesson_rated', {
        status: 'removed',
        lesson_id: this.lesson.id,
        previous_rating: previousRating ? 'liked' : 'disliked',
      });

      appClient.deleteRating(this.lesson.id).catch(() => {
        this.$store.dispatch('lesson/setIsLiked', previousRating);
        if (this.lesson) {
          this.lesson.is_liked = previousRating;
        }
      });
    },
    rateLesson(isLiked) {
      const previousRating = this.is_liked;

      this.$store.dispatch('lesson/setIsLiked', isLiked);

      if (this.lesson) {
        this.lesson.is_liked = isLiked;
      }

      this.popupOpened = isLiked === false;

      const language = this.$i18n ? this.$i18n.locale : document.documentElement.lang || 'en';

      this.trackEvent('lesson_rated', {
        status: 'added',
        lesson_id: this.lesson.id,
        rating: isLiked ? 'liked' : 'disliked',
      });

      appClient
        .rateLesson(this.lesson.id, {
          is_liked: isLiked,
          language: language,
        })
        .catch(() => {
          this.$store.dispatch('lesson/setIsLiked', previousRating);
          if (this.lesson) {
            this.lesson.is_liked = previousRating;
          }
        });
    },
    openPopup() {
      this.popupOpened = true;
      this.trackEvent('button_clicked', {
        button_name: 'rating_popup',
        lesson_id: this.lesson.id,
      });
    },
    closePopup() {
      this.popupOpened = false;
      this.trackEvent('button_clicked', {
        button_name: 'close_rating_popup',
        lesson_id: this.lesson.id,
      });
    },
    submitFeedback() {
      const language = this.$i18n ? this.$i18n.locale : document.documentElement.lang || 'en';

      this.trackEvent('button_clicked', {
        button_name: 'submit_feedback',
        lesson_id: this.lesson.id,
      });

      appClient
        .rateLesson(this.lesson.id, {
          is_liked: false,
          feedback: this.feedback,
          language: language,
        })
        .catch(() => {
          this.$store.dispatch('lesson/setIsLiked', true);
        });

      this.popupOpened = false;
      this.feedback = '';
    },
  },
};
</script>

<style lang="scss" scoped>
.lesson-rating {
  position: relative;

  &__buttons {
    display: flex;
    justify-content: space-between;
    position: relative;
    background-color: var(--gray-8);
    width: auto;
    z-index: 300;
    border-radius: 8px;
    height: 55px;

    @include tablet-landscape {
      height: 34px;
    }
  }

  &__text {
    font-size: 14px;
    color: #e5e6eb;
    margin: 0;
    display: block;

    @include tablet-landscape {
      display: none;
    }
  }

  &__icon {
    font-size: 18px;
    padding: 6px 16px;
    color: #e5e6eb;
    height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    @include tablet-landscape {
      height: 34px;
    }

    &--desktop {
      display: none;
      @include tablet-landscape {
        display: block;
      }
    }
    &--mobile {
      border-inline-start: 0 !important;
      display: flex;
      border-radius: 8px !important;
      background-color: var(--gray-7);
      width: 72px;
      padding: 6px 16px;
      .rtl & {
        padding: 6px 10px;
      }
      @include tablet-landscape {
        display: none;
      }
    }

    &:hover {
      background-color: var(--gray-6);
      cursor: pointer;
      border-start-start-radius: 8px;
      border-end-start-radius: 8px;
    }
  }

  &__popup {
    position: absolute;
    background-color: var(--gray-6);
    padding: 20px;
    border-radius: 8px;
    z-index: 10000;
    width: fit-content;
    bottom: 66px;
    inset-inline-end: 0px;
    gap: 16px;
    display: flex;
    flex-direction: column;
    @include tablet-landscape {
      bottom: 40px;
    }

    &--large {
      max-width: 400px;
      width: calc(100vw - 30px);
    }
  }

  &__popup-buttons {
    display: flex;
    justify-content: center;
    flex-direction: row;
    cursor: pointer;
  }

  &__popup-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #76767a;
    min-width: 90px;
    padding: 0 12px;

    & > p {
      font-size: 11px;
      margin: 0;
      text-align: center;
      white-space: nowrap;
    }

    &.white {
      color: white;
      & > p {
        color: white;
      }
    }
  }

  &__popup-close {
    width: 60px;
    height: 50px;
    background-color: var(--gray-6);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 100px;
    cursor: pointer;
    font-size: 20px;
    z-index: 10000;
    position: relative;

    @include tablet-landscape {
      height: 34px;
      font-size: 16px;
    }
    &:hover {
      background-color: var(--gray-5);
    }
  }

  &__popup-icon {
    font-size: 22px;
    &--invert {
      transform: scaleX(-1);
    }
  }

  &__popup-input {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 8px;

    & > textarea {
      flex-grow: 1;
      background-color: transparent;
      border: 1px solid #474747;
      border-radius: 8px;
      padding: 8px 13px;
      font-size: 14px;
      color: #fafafa;
      min-height: 52px;
      max-height: 132px;
      outline: none;
      resize: none;
      overflow-y: auto;
      line-height: 1.2;

      &::-webkit-scrollbar {
        display: none;
      }
    }

    & > button {
      background-color: white;
      color: black;
      padding: 0;
      border: 0;
      border-radius: 50%;
      width: 30px;
      height: 30px;
      margin-bottom: 2px;
      font-size: 14px;
      cursor: pointer;
      &:disabled {
        background-color: var(--gray-4);
      }
    }
  }

  &__popup-overlay {
    position: fixed;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    top: 0;
    inset-inline-start: 0;
    z-index: 9999;
  }
}

.lesson-rating__icon--down {
  &:hover {
    border-start-end-radius: 8px;
    border-end-end-radius: 8px;
  }
}

.lesson-rating__icon--invert {
  transform: scaleX(-1);
}

.lesson-rating__icon + .lesson-rating__icon {
  border-inline-start: 1px solid var(--gray-5);
}
</style>
